import { AbstractCollection } from '../abstract/Collection';
import { AbstractDocument, Identifiable, Timestampable, Untrackable } from '../abstract/Document';
import type { ISupplier, ISupplier_Type } from './Suppliers.types';
import { Achievements } from './Suppliers/Achievements';
import { Admins } from './Suppliers/Admin';
import { Ads } from './Suppliers/Ads';
import { Apps } from './Suppliers/Apps';
import { Badges } from './Suppliers/Badges';
import { Brochures } from './Suppliers/Brochures';
import { Calendars } from './Suppliers/Calendars';
import { Configurations } from './Suppliers/Configurations';
import { Contacts } from './Suppliers/Contacts';
import { Fairs } from './Suppliers/Fairs';
import { FeedbackCollection } from './Suppliers/Feedback';
import { Files } from './Suppliers/Files';
import { Leads } from './Suppliers/Leads';
import { Offers } from './Suppliers/Offers';
import { Packages } from './Suppliers/Packages';
import { Photos } from './Suppliers/Photos';
import { Prices } from './Suppliers/Prices';
import { Questions } from './Suppliers/Questions';
import { Recommendations } from './Suppliers/Recommendations';
import { Relations } from './Suppliers/Relations';
import { Reports } from './Suppliers/Reports';
import { Users } from './Suppliers/Users';
import { Videos } from './Suppliers/Videos';
import { Works } from './Suppliers/Works';
import gazetteer from '@bridebook/toolbox/src/gazetteer';

@Identifiable
@Timestampable
@Untrackable
export class Supplier extends AbstractDocument<ISupplier> {
  readonly collections = {
    Achievements: new Achievements(this),
    Admins: new Admins(this),
    Ads: new Ads(this),
    Apps: new Apps(this),
    Badges: new Badges(this),
    Brochures: new Brochures(this),
    Calendars: new Calendars(this),
    Configurations: new Configurations(this),
    Contacts: new Contacts(this),
    Fairs: new Fairs(this),
    Feedback: new FeedbackCollection(this),
    Files: new Files(this),
    Leads: new Leads(this),
    Offers: new Offers(this),
    Packages: new Packages(this),
    Photos: new Photos(this),
    Prices: new Prices(this),
    Questions: new Questions(this),
    Recommendations: new Recommendations(this),
    Relations: new Relations(this),
    Reports: new Reports(this),
    Users: new Users(this),
    Videos: new Videos(this),
    Works: new Works(this),
  };

  readonly translatable = (data: ISupplier): NonNullable<ISupplier['_translations']>[string] => ({
    company: {
      manager: {
        welcomeMessage: data.company?.manager?.welcomeMessage,
      },
    },
    description: data.description,
    directions: data.directions,
    status: {
      message: data.status?.message,
    },
    bridebook: {
      offer: data.bridebook?.offer,
    },
  });

  /**
   * Get `admin` document from `admins` sub-collection.
   */
  getAdmin() {
    return this.collections.Admins.getAdmin();
  }

  /**
   * Get `chargebee` document from `admins` sub-collection.
   */
  getChargebee() {
    return this.collections.Admins.getChargebee();
  }

  /**
   * Get `ledger` document from `admins` sub-collection.
   */
  getLedger() {
    return this.collections.Admins.getLedger();
  }

  /**
   * Get `score` document from `admins` sub-collection.
   */
  getScore() {
    return this.collections.Admins.getScore();
  }

  /**
   * Get `stats` document from `admins` sub-collection.
   */
  getStats() {
    return this.collections.Admins.getStats();
  }

  /**
   * Get `stripe` document from `admins` sub-collection.
   */
  getStripe() {
    return this.collections.Admins.getStripe();
  }

  /**
   * Get first photo ordered by `order` prop
   */
  getThumbnail() {
    return this.collections.Photos.getFirst();
  }

  getURL(data: ISupplier, domain = 'https://bridebook.com') {
    let result = domain;
    // "group" type in not defined here because we don't want groups
    // to be available on the couple side
    const categories: Partial<Record<ISupplier_Type, string>> = {
      beauty: 'wedding-beauty-hair-makeup',
      cakes: 'wedding-cakes',
      catering: 'wedding-catering',
      decoration: 'wedding-decoration-hire',
      dress: 'wedding-dress-accessories',
      entertainment: 'wedding-entertainment',
      florist: 'wedding-florists',
      jewellery: 'wedding-rings-jewellery',
      marquee: 'wedding-marquee-hire',
      menswear: 'wedding-menswear',
      music: 'wedding-musicians-bands-djs',
      photo: 'wedding-photographers',
      planners: 'wedding-planners-toastmasters-celebrants',
      stationery: 'wedding-stationery',
      transport: 'wedding-transport',
      venue: 'wedding-venues',
      video: 'wedding-videographers',
    };

    const market = gazetteer.getMarketByCountry(data.l10n.country);

    result += '/' + market.prefix;
    result += '/' + categories[data.type[0] as keyof typeof categories];
    result += '/' + data.name.toLowerCase().replace(/[^-0-9a-z]+/g, '-');
    result += '-' + data.address.city.toLowerCase().replace(/[^-0-9a-z]+/g, '-');
    result += '-' + data.address.adminArea[0]?.toLowerCase().replace(/[^-0-9a-z]+/g, '-');
    result += '-' + data.publicId;

    return result;
  }
}

export class Suppliers extends AbstractCollection<Supplier, ISupplier> {
  static path = 'suppliers';

  constructor() {
    super(Suppliers.path, Supplier);
  }

  static get _() {
    return new this();
  }
}
